import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Http from '../../core/Http';
import { useTheme } from '../contexts/ThemeContext';
import { DEFAULT_RELATED_SITES, normalizeRelatedSitesPayload } from '../constants/relatedSitesDefault';
import { localizeRelatedSitesPayload } from '../utils/comm/relatedSitesLocalized';

/**
 * 협력 기관(연관 사이트) 캐러셀 데이터 — 공개 API 우선, 실패 시 기본값
 */
const useRelatedSites = () => {
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || (typeof window !== 'undefined' && window.__themeConfig) || {};
  const workspaceId = Number(themeConfig.workspaceId) || 1;
  const { i18n } = useTranslation();

  const [rawRelatedSitesData, setRawRelatedSitesData] = useState(null);

  const relatedSitesData = useMemo(
    () => (rawRelatedSitesData ? localizeRelatedSitesPayload(rawRelatedSitesData, i18n.language) : null),
    [rawRelatedSitesData, i18n.language]
  );

  const fetchRelatedSites = useCallback(async () => {
    try {
      const res = await Http.get(`/api/comm/related-sites/workspace/${workspaceId}/latest`);
      if (res && typeof res === 'object' && Array.isArray(res.sites)) {
        const normalized = normalizeRelatedSitesPayload(res);
        setRawRelatedSitesData(normalized);
        if (typeof window !== 'undefined') {
          window.relatedSites = normalized;
        }
        return;
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 404 && status !== 204) {
        console.warn('[useRelatedSites] API 조회 실패, 기본값 사용:', e?.message || e);
      }
    }
    const fallback = normalizeRelatedSitesPayload(
      typeof window !== 'undefined' && window.relatedSites && Array.isArray(window.relatedSites.sites)
        ? window.relatedSites
        : DEFAULT_RELATED_SITES
    );
    setRawRelatedSitesData(fallback);
    if (typeof window !== 'undefined') {
      window.relatedSites = fallback;
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchRelatedSites();
  }, [fetchRelatedSites]);

  useEffect(() => {
    const onSaved = () => {
      fetchRelatedSites();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('glow-related-sites-saved', onSaved);
      return () => window.removeEventListener('glow-related-sites-saved', onSaved);
    }
    return undefined;
  }, [fetchRelatedSites]);

  return relatedSitesData;
};

export default useRelatedSites;
